import React, { createContext, useContext, useMemo, useState, useCallback } from "react";
import { trackAddToCart } from "@/lib/metaPixel";

const CartContext = createContext(null);

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]); // { product, qty }
  const [isOpen, setIsOpen] = useState(false);

  const openCart = useCallback(() => setIsOpen(true), []);
  const closeCart = useCallback(() => setIsOpen(false), []);

  const stockOf = (p) => (typeof p?.quantityAvailable === "number" ? p.quantityAvailable : null);

  const addItem = useCallback((product, qty = 1) => {
    trackAddToCart({ id: product.id, name: product.name, price: product.price });
    setItems((prev) => {
      const existing = prev.find((i) => i.product.id === product.id);
      const stock = stockOf(product);
      const currentQty = existing ? existing.qty : 0;
      const nextQty = stock !== null ? Math.min(currentQty + qty, stock) : currentQty + qty;
      if (existing) {
        return prev.map((i) =>
          i.product.id === product.id ? { ...i, product, qty: nextQty } : i
        );
      }
      return [...prev, { product, qty: Math.min(qty, stock ?? qty) }];
    });
  }, []);

  const removeItem = useCallback((productId) => {
    setItems((prev) => prev.filter((i) => i.product.id !== productId));
  }, []);

  const updateQty = useCallback((productId, delta) => {
    setItems((prev) =>
      prev
        .map((i) => {
          if (i.product.id !== productId) return i;
          const stock = stockOf(i.product);
          const nextQty = i.qty + delta;
          const capped = stock !== null ? Math.min(nextQty, stock) : nextQty;
          return { ...i, qty: Math.max(0, capped) };
        })
        .filter((i) => i.qty > 0)
    );
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const cartQty = useCallback(
    (productId) => items.find((i) => i.product.id === productId)?.qty || 0,
    [items]
  );

  const count = useMemo(() => items.reduce((sum, i) => sum + i.qty, 0), [items]);
  const subtotal = useMemo(
    () => items.reduce((sum, i) => sum + i.product.price * i.qty, 0),
    [items]
  );

  const value = {
    items,
    isOpen,
    openCart,
    closeCart,
    addItem,
    removeItem,
    updateQty,
    clearCart,
    cartQty,
    count,
    subtotal,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
